import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadSelectedImagesAsZip,
  downloadImagesAsZip as downloadZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var41');

            const leftImage = new Image();
            leftImage.classList.add('left-image-var41');
            leftImage.src = variation.imageSrc;
            imageHolder.appendChild(leftImage);

            const watermark = createWatermarkTagImage(
              'actual-photo-var41',
              variation.watermarkTag,
              variation.watermarkPosition,
            );
            if (watermark) imageHolder.appendChild(watermark);

            if (leftImage.decode) {
              try {
                await leftImage.decode();
              } catch {}
            } else {
              await new Promise<void>((res) => {
                if (leftImage.complete) return res();
                leftImage.onload = () => res();
                leftImage.onerror = () => res();
              });
            }

            const chatsHolder = document.createElement('div');
            chatsHolder.classList.add('chats-holder-var41');
            imageHolder.appendChild(chatsHolder);

            // Wait for font to load before measuring text
            await document.fonts.ready;

            variation.chats.forEach((chat) => {
              if (chat.bottomText) {
                const bottomText = document.createElement('div');
                bottomText.classList.add('bottom-text-var41');
                bottomText.classList.add(`bottom-text-var41__${chat.color}`);
                if (variation.chats.length === 1) {
                  bottomText.classList.add('bottom-text-var41__center');
                }
                let text = chat.bottomText;
                if (chat.brakes) {
                  bottomText.innerText = text;
                } else {
                  text = distributeTextCanvas(`${text}`, {
                    maxWidth: 460,
                    fontFamily: 'SourceSansProRegular',
                    minFontSize:
                      variation.fontSize !== 0 ? variation.fontSize : 40,
                  });
                  bottomText.innerHTML = text;
                }
                chatsHolder.appendChild(bottomText);
                if (variation.fontSize) {
                  bottomText.style.fontSize = `${variation.fontSize}px`;
                }
              }
            });

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var41');

              imageHolder.appendChild(middleBreak);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var41',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
